import { registerLocaleVariant } from "../registry";
import { makeId } from "../helpers";
import type { ArticleBody } from "../types";

// NOTE: These FR variants are NOT faithful translations of the EN articles.
// The EN base articles describe the US healthcare system (state mandates,
// ERISA, Progyny/Carrot, HSA/FSA). The FR content below has been rewritten
// for the French context: Sécurité sociale (ALD 31 hors liste, prise en
// charge à 100 % jusqu'à 43 ans, 4 FIV + 6 IIU), mutuelles, parcours
// transfrontaliers, autoconservation sociétale post-loi 2021. Verify
// regulatory details with a clinician before shipping.

// ─── Helper ─────────────────────────────────────────────────────────

function frMoonArticle(
  slug: string,
  variant: {
    title: string;
    subtitle: string;
    body: ArticleBody;
  },
): void {
  registerLocaleVariant(makeId(`moon-article-${slug}`), "fr", variant);
}

// ─── Articles ───────────────────────────────────────────────────────

frMoonArticle("insurance-fertility", {
  title: "Sécurité sociale et fertilité : ce qui est pris en charge",
  subtitle:
    "Comprendre l'ALD, la prise en charge à 100 %, et les limites du remboursement.",
  body: {
    introduction: "Couverture",
    quickSummary: [
      {
        title: "100 % pris en charge dans le parcours d'AMP.",
        description:
          "Si tu as moins de 43 ans et que tu es prise en charge dans un centre d'AMP agréé, l'AMP est remboursée à 100 % du tarif Sécurité sociale.",
      },
      {
        title: "4 FIV, 6 IIU.",
        description:
          "La loi française autorise 4 tentatives de FIV (avec leurs TEC) et 6 inséminations remboursées, qui se réinitialisent après une naissance vivante.",
      },
      {
        title: "L'ALD est la clé.",
        description:
          "Ton médecin doit demander l'admission au régime ALD (affection longue durée) pour activer le remboursement intégral et l'exonération du ticket modérateur.",
      },
    ],
    sections: [
      {
        title: "Qui peut bénéficier de la prise en charge",
        content:
          "Depuis la loi de bioéthique de 2021, l'accès à l'AMP en France est ouvert aux femmes seules et aux couples de femmes, en plus des couples hétérosexuels. La prise en charge financière par la Sécurité sociale est la même pour toutes les situations, dès lors que tu es affiliée au régime français.",
        bulletPoints: [
          "Femmes seules, couples de femmes, couples hétérosexuels : même accès, même remboursement",
          "Limite d'âge : avant le 43e anniversaire de la femme",
          "Affiliation à un régime français de Sécurité sociale (ou ressortissante UE avec carte européenne)",
          "Suivi dans un centre d'AMP agréé par l'Agence de la biomédecine",
        ],
      },
      {
        title: "Le rôle du dispositif ALD",
        content:
          "L'AMP n'est pas une ALD officielle au sens classique (les « 30 ALD »), mais elle bénéficie d'un régime d'ALD dit « hors liste » (ALD 31). C'est ton médecin de la reproduction qui fait la demande à ta CPAM. Une fois acceptée, tu es exonérée du ticket modérateur sur tous les actes liés à l'AMP — c'est ce qui permet la prise en charge à 100 %.",
        bulletPoints: [
          "Ta médecin remplit un protocole de soins ALD (Cerfa S3501)",
          "La demande est envoyée à la CPAM, qui répond en quelques semaines",
          "Une fois validée, tous les actes liés à l'AMP sont remboursés à 100 %",
          "L'ALD couvre aussi les transports liés au parcours, sous conditions",
        ],
      },
      {
        title: "Ce qui est remboursé",
        content:
          "Une fois dans le parcours, le remboursement est très large. La majorité des patientes ne paient rien de leur poche pour les actes médicaux.",
        bulletPoints: [
          "Bilan d'infertilité : bilan hormonal, échographies, hystérosalpingographie, spermogramme",
          "Stimulation ovarienne (médicaments inclus, à de rares exceptions près)",
          "Ponction ovocytaire et anesthésie",
          "Travail de laboratoire : fécondation, culture, vitrification d'embryons surnuméraires",
          "Transfert d'embryon frais et transferts d'embryons congelés (TEC) ultérieurs",
          "Consultations de suivi avec ta médecin de la reproduction",
        ],
      },
      {
        title: "Ce qui n'est pas (ou mal) remboursé",
        content:
          "Quelques angles morts à connaître. Ils sont rares, mais peuvent surprendre si on ne les anticipe pas.",
        bulletPoints: [
          "5e cycle de FIV et au-delà : à payer en intégralité",
          "Traitement après 43 ans : non pris en charge",
          "Préservation de fertilité « sociétale » (autoconservation d'ovocytes hors raison médicale) : à ta charge depuis la loi de 2021, comptes-y 2 500 à 5 000 €",
          "Certains compléments ou médicaments hors liste : prescription au cas par cas",
          "PGT-A : non disponible en routine en France, encadré strictement (indications limitées)",
          "Suivi psychologique en dehors du centre : selon contrat de mutuelle",
        ],
      },
      {
        title: "Les médicaments",
        content:
          "La plupart des médicaments de stimulation (gonadotrophines comme Gonal-F, Puregon, Menopur ; antagonistes comme Cetrotide, Orgalutran ; déclencheurs Ovitrelle) sont remboursés à 100 % dans le cadre de l'ALD. Quelques produits hors liste, ou des dosages particuliers, peuvent ne pas être pris en charge — ta médecin et ta pharmacienne peuvent te confirmer ça à l'avance.",
      },
      {
        title: "Le rôle de la mutuelle",
        content:
          "Dans un parcours conventionné secteur 1, la Sécu couvre 100 % du tarif et la mutuelle est rarement nécessaire pour les actes médicaux. Mais elle peut compléter : ticket modérateur sur actes hors ALD, dépassements d'honoraires en secteur 2, soins de support (acupuncture, sophrologie, suivi psy). Lis bien le poste « actes hors nomenclature » et « médecines douces » de ton contrat.",
      },
      {
        title: "Public vs privé : secteur 1 vs secteur 2",
        content:
          "Tu peux choisir un centre d'AMP public ou un centre privé conventionné secteur 1 ou 2. En secteur 1, pas de dépassement d'honoraires : la Sécu couvre 100 %. En secteur 2, le médecin peut facturer un dépassement non remboursé par la Sécu — ta mutuelle peut le compléter, selon ton contrat. Le parcours médical (médicaments, biologie) reste identique entre public et privé : la qualité ne dépend pas du secteur mais du centre.",
      },
      {
        title: "Si tu reçois un refus de prise en charge",
        content:
          "Les refus sont rares mais arrivent — typiquement pour une demande tardive d'ALD, un dépassement de cycle, ou une indication contestée. Tu as le droit de contester. Demande d'abord le motif écrit du refus, puis sollicite ta médecin de la reproduction pour rédiger un courrier de soutien. Tu peux saisir la commission de recours amiable de ta CPAM dans un délai de 2 mois.",
      },
    ],
    references: [
      {
        title: "L'assistance médicale à la procréation (AMP)",
        authors: "Assurance Maladie (ameli.fr)",
        url: "https://www.ameli.fr/assure/remboursements/rembourse/assistance-medicale-la-procreation-amp/prise-en-charge-de-l-assistance-medicale-la-procreation-amp",
        evidenceLevel: "official",
      },
      {
        title: "Procréation médicalement assistée (PMA) — fiche d'information",
        authors: "Service Public",
        url: "https://www.service-public.gouv.fr/particuliers/vosdroits/F31462",
        evidenceLevel: "official",
      },
      {
        title: "Code de la santé publique — articles L2141-1 à L2141-12 (AMP)",
        authors: "Légifrance",
        url: "https://www.legifrance.gouv.fr/codes/section_lc/LEGITEXT000006072665/LEGISCTA000006171144/",
        evidenceLevel: "official",
      },
      {
        title: "Loi n° 2021-1017 du 2 août 2021 relative à la bioéthique",
        authors: "Légifrance",
        year: 2021,
        url: "https://www.legifrance.gouv.fr/loda/id/JORFTEXT000043884384/",
        evidenceLevel: "official",
      },
      {
        title: "Affection longue durée (ALD) : protocole de soins",
        authors: "Assurance Maladie (ameli.fr)",
        url: "https://www.ameli.fr/assure/droits-demarches/maladie-accident-hospitalisation/affection-longue-duree-ald/affection-longue-duree-maladie-chronique",
        evidenceLevel: "official",
      },
    ],
  },
});

frMoonArticle("employer-benefits", {
  title: "Aides employeur en France",
  subtitle: "Ce que ton entreprise peut faire, au-delà de la Sécu.",
  body: {
    introduction: "Avantages",
    quickSummary: [
      {
        title: "La Sécu couvre l'essentiel.",
        description:
          "Contrairement aux États-Unis, la majorité des frais d'AMP sont déjà couverts par la Sécurité sociale en France. Les aides employeur sont rares mais peuvent compléter.",
      },
      {
        title: "Le droit du travail te protège.",
        description:
          "Le Code du travail prévoit des absences autorisées pour les rendez-vous d'AMP, sans avoir à justifier le motif à ton employeur.",
      },
      {
        title: "Quelques entreprises commencent.",
        description:
          "Des grandes entreprises françaises et internationales commencent à proposer un soutien spécifique : suivi psy, plateformes de coaching, voire prise en charge de frais hors Sécu.",
      },
    ],
    sections: [
      {
        title: "Tes droits au travail",
        content:
          "Tu as le droit de t'absenter pour les actes médicaux nécessaires à ton parcours d'AMP. Ces absences sont protégées par l'article L1225-16 du Code du travail, mis à jour par la loi de bioéthique de 2021. Ton ou ta partenaire peut aussi s'absenter pour t'accompagner à trois rendez-vous au maximum. Tu n'as pas à dire à ton employeur que c'est lié à l'AMP — un certificat médical suffit. La protection contre le licenciement liée à la grossesse est encadrée par l'article L1225-4 du Code du travail : elle s'applique pendant toute la grossesse et au minimum pendant les 10 semaines suivant la fin du congé maternité.",
        bulletPoints: [
          "Absences autorisées pour les actes médicaux d'AMP, sans perte de rémunération (article L1225-16)",
          "Pas d'obligation de motiver le contenu médical à l'employeur",
          "Trois absences possibles pour le ou la partenaire qui t'accompagne",
          "Protection contre le licenciement : pendant toute la grossesse et 10 semaines après le congé maternité (article L1225-4)",
        ],
      },
      {
        title: "Les rares avantages d'entreprise",
        content:
          "Quelques entreprises françaises et internationales basées en France commencent à proposer des dispositifs complémentaires. C'est encore peu répandu, mais cela évolue. Demande à tes RH ou à ton CSE si quelque chose existe — ça ne coûte rien et peut surprendre.",
        bulletPoints: [
          "Mutuelle d'entreprise plus généreuse sur les actes hors Sécu (suivi psy, médecines douces)",
          "Plateformes de coaching ou de téléconsultation incluses dans les avantages sociaux",
          "Aide à la garde d'enfants pour faciliter les rendez-vous (rare)",
          "Forfait bien-être ou jours de congé supplémentaires (très rare)",
        ],
      },
      {
        title: "Si tu travailles pour une entreprise internationale",
        content:
          "Si ton employeur est une multinationale (souvent américaine ou britannique), il peut proposer un programme global de fertilité (Progyny, Carrot, Maven, Hertility…). En France, ces programmes peuvent compléter la Sécurité sociale pour les frais non couverts : autoconservation sociétale, dépassements d'honoraires, suivi psy, ou les cycles au-delà des 4 remboursés. Vérifie ton portail RH ou ton intranet, ce n'est presque jamais communiqué activement.",
      },
      {
        title: "Comment en parler à ton employeur",
        content:
          "Tu n'as aucune obligation légale d'informer ton employeur de ton parcours d'AMP. Mais si tu choisis de le faire — pour expliquer des absences plus fréquentes ou demander de la flexibilité — sache qu'une discussion avec les RH (et pas seulement ton ou ta manager) est souvent plus protectrice. Tu peux aussi te rapprocher de la médecine du travail, qui est tenue à la confidentialité.",
      },
    ],
    references: [
      {
        title: "Article L1225-16 du Code du travail (absences pour AMP)",
        authors: "Légifrance",
        url: "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000043898337/",
        evidenceLevel: "official",
      },
      {
        title:
          "Article L1225-4 du Code du travail (protection contre le licenciement)",
        authors: "Légifrance",
        url: "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000033019749/",
        evidenceLevel: "official",
      },
      {
        title: "Absences pour examens prénataux ou AMP",
        authors: "Service Public",
        url: "https://www.service-public.fr/particuliers/vosdroits/F2265",
        evidenceLevel: "official",
      },
    ],
  },
});

frMoonArticle("financing-ivf", {
  title: "Financer la FIV : prêts, bourses, et plans de paiement",
  subtitle:
    "Quand l'assurance ne couvre pas — les options pour payer de sa poche.",
  body: {
    introduction: "Argent",
    quickSummary: [
      {
        title: "La Sécu fait l'essentiel.",
        description:
          "En France, la FIV dans le parcours conventionné est remboursée à 100 %. Le besoin de financement personnel est rare et concerne surtout des situations très précises.",
      },
      {
        title: "3 000 à 6 000 € si tu paies de ta poche.",
        description:
          "Quand tu sors du cadre remboursé (5e cycle, traitement après 43 ans, autoconservation sociétale), compte 3 000 à 6 000 € par tentative en France.",
      },
      {
        title: "Plusieurs leviers en cas de gros reste à charge.",
        description:
          "Mutuelle, prêt personnel à la consommation, parcours à l'étranger : à comparer avant de t'engager.",
      },
    ],
    sections: [
      {
        title: "Quand le financement personnel devient nécessaire",
        content:
          "Dans la plupart des parcours d'AMP en France, tu n'auras pratiquement rien à payer. Ce n'est qu'à certains moments précis que la question du financement personnel se pose vraiment.",
        bulletPoints: [
          "Quand tu dépasses les 4 tentatives de FIV remboursées",
          "Quand tu as plus de 43 ans et que tu poursuis un traitement en France (rare) ou à l'étranger",
          "Quand tu fais une autoconservation sociétale d'ovocytes (sans raison médicale)",
          "Quand tu choisis un parcours à l'étranger pour d'autres raisons (PGT-A, GPA, parcours plus rapide)",
          "Quand certains médicaments ou actes ne sont pas remboursés (rare)",
        ],
      },
      {
        title: "Le coût d'un cycle hors Sécu en France",
        content:
          "Si tu paies de ta poche un cycle de FIV en France (par exemple un 5e cycle ou après 43 ans en privé), compte en général 3 000 à 6 000 € selon le centre et la complexité. C'est nettement moins que les tarifs américains (15 000 à 30 000 $), mais ça reste un budget important.",
        bulletPoints: [
          "Cycle de FIV complet hors Sécu : environ 3 000 à 5 000 €",
          "Médicaments de stimulation (si non remboursés) : 500 à 1 500 €",
          "Anesthésie pour la ponction : généralement incluse dans le forfait",
          "Conservation d'embryons surnuméraires hors ALD : environ 50 à 100 € par an",
          "PGT-A (très rarement disponible en France et sous conditions strictes) : à l'étranger, environ 3 000 €",
        ],
      },
      {
        title: "Le rôle de la mutuelle",
        content:
          "Avant d'envisager un prêt, vérifie ce que ta mutuelle prend en charge au-delà de la Sécu. Beaucoup de mutuelles couvrent les dépassements d'honoraires, les actes hors nomenclature, ou des forfaits annuels pour les soins de support (psy, acupuncture). Certaines mutuelles haut de gamme proposent même un forfait spécifique fertilité. Ça ne paie pas tout, mais ça peut alléger sensiblement.",
      },
      {
        title: "Prêts personnels et facilités de paiement",
        content:
          "Les banques françaises ne proposent pas de « prêt fertilité » dédié comme aux États-Unis, mais un prêt personnel à la consommation peut financer un cycle hors Sécu. Compare les TAEG (souvent entre 3 % et 7 % en France) et la durée. Certains centres privés proposent un paiement échelonné sur 3 à 6 mois, parfois plus — demande explicitement.",
        bulletPoints: [
          "Prêt personnel banque traditionnelle ou en ligne : compare les TAEG",
          "Facilité de paiement directe avec le centre : demande, ça se négocie",
          "Crédit auprès de ta mutuelle : certaines mutuelles offrent ce service",
          "Évite les organismes qui ciblent spécifiquement la fertilité avec des taux opaques",
        ],
      },
      {
        title: "Le parcours à l'étranger",
        content:
          "Certaines patientes choisissent de poursuivre en Espagne, en République tchèque, en Belgique, ou en Grèce — pour accéder à un parcours qui n'existe pas en France (PGT-A, don anonyme historique, GPA), pour aller plus vite, ou parce qu'elles ont dépassé la limite d'âge en France. Les coûts sont généralement entre 4 000 et 8 000 € par cycle, à comparer avec une éventuelle prise en charge en France. À noter : depuis 2021, les enfants nés de dons en France ont accès aux origines à 18 ans — si ton parcours implique un don, ça vaut le coup de poser la question pour chaque pays.",
        bulletPoints: [
          "Espagne : très populaire, parcours rapide, large offre de dons",
          "Belgique : proche, parcours rapide, prise en charge partielle possible si tu es transfrontalière",
          "République tchèque, Grèce, Portugal : tarifs souvent plus accessibles",
          "Prends en compte : coût total avec déplacements et hébergement, suivi médical à distance, cadre légal local",
        ],
      },
      {
        title: "Associations et soutien financier",
        content:
          "Il existe peu de bourses « fertilité » en France comme aux États-Unis, parce que la Sécu couvre déjà l'essentiel. Mais des associations peuvent t'orienter ou t'aider dans des cas particuliers : MAIA, le Collectif BAMP. Elles proposent aussi un soutien moral et un réseau de patientes qui ont vécu les mêmes parcours.",
      },
    ],
    references: [
      {
        title: "L'assistance médicale à la procréation (AMP)",
        authors: "Assurance Maladie (ameli.fr)",
        url: "https://www.ameli.fr/assure/remboursements/rembourse/assistance-medicale-la-procreation-amp/prise-en-charge-de-l-assistance-medicale-la-procreation-amp",
        evidenceLevel: "official",
      },
      {
        title: "Autoconservation des ovocytes en France",
        authors: "Agence de la biomédecine",
        url: "https://www.procreation-medicale.fr/parcours-congeler-ovocytes/",
        evidenceLevel: "official",
      },
      {
        title: "Statistiques sur les taux des crédits à la consommation",
        authors: "Banque de France",
        url: "https://www.banque-france.fr/statistiques",
        evidenceLevel: "official",
      },
      {
        title: "Collectif BAMP — communauté patientes fertilité",
        authors: "Collectif BAMP",
        url: "https://www.bamp.fr/",
        evidenceLevel: "reference",
      },
    ],
  },
});

frMoonArticle("hidden-costs", {
  title: "Les coûts cachés dont personne ne parle",
  subtitle:
    "Frais de stockage, options supplémentaires, et les dépenses qui prennent les gens au dépourvu.",
  body: {
    introduction: "Sans filtre",
    quickSummary: [
      {
        title: "Le hors-parcours s'accumule.",
        description:
          "La Sécu couvre l'essentiel pendant le parcours d'AMP. Mais tout ce qui sort de ce cadre — conservation longue, soins de support, options supplémentaires — finit par compter.",
      },
      {
        title: "Les actes hors nomenclature surprennent.",
        description:
          "Acupuncture, sophrologie, ostéopathie, suivi psy au privé, certains compléments : aucun n'est remboursé par la Sécu mais s'additionne rapidement.",
      },
      {
        title: "Au-delà de la facture médicale.",
        description:
          "La perte de revenus, les déplacements, et la charge mentale sont des coûts réels mais rarement discutés.",
      },
    ],
    sections: [
      {
        title: "La conservation après le parcours",
        content:
          "Pendant ton parcours d'AMP dans le cadre de l'ALD, la conservation d'embryons surnuméraires est prise en charge. Mais une fois ton projet parental concrétisé (naissance vivante) ou abandonné, la conservation au-delà bascule en hors-Sécu — ou doit faire l'objet d'une décision (don, recherche, destruction). C'est un moment souvent évité, mais important à anticiper.",
        bulletPoints: [
          "Conservation hors ALD : environ 50 à 100 € par an, à régler au CECOS",
          "Décision annuelle obligatoire : tu dois confirmer chaque année si tu veux poursuivre la conservation, donner, ou détruire",
          "Sans réponse pendant deux relances, les embryons peuvent être détruits par défaut",
          "Le don aux couples ou à la recherche est encadré et anonymisé",
        ],
      },
      {
        title: "Médicaments hors liste",
        content:
          "La majorité des médicaments de stimulation (Gonal-F, Puregon, Menopur, Cetrotide, Orgalutran, Ovitrelle…) sont remboursés à 100 % dans le cadre de l'ALD AMP. Mais quelques produits ou dosages particuliers peuvent ne pas être pris en charge, ou seulement à 65 %. Demande à ta pharmacie ou à ta médecin de vérifier en amont.",
        bulletPoints: [
          "Compléments alimentaires (oméga-3, vitamine D, CoQ10) : rarement remboursés",
          "Certaines hormones thyroïdiennes ou suppléments hors AMM : variable",
          "Probiotiques, plantes médicinales : non remboursés",
          "Si un médicament te paraît cher en pharmacie, demande à ta médecin s'il existe une alternative remboursée",
        ],
      },
      {
        title: "Soins de support et thérapies complémentaires",
        content:
          "Beaucoup de patientes ajoutent à leur parcours d'AMP des soins qui les aident à mieux vivre l'attente, l'incertitude, et les effets secondaires. La Sécu ne les couvre pas, mais ils ont une vraie valeur. Compte-les dans ton budget si tu y as recours régulièrement.",
        bulletPoints: [
          "Acupuncture spécialisée fertilité : 50 à 80 € la séance, fréquence variable",
          "Sophrologie et hypnose : 60 à 100 € la séance",
          "Ostéopathie : 50 à 80 € la séance",
          "Suivi psychologique en libéral : 60 à 100 € la séance (parfois remboursé par Mon soutien psy ou ta mutuelle)",
          "Yoga fertilité, ateliers de groupe : variable",
        ],
      },
      {
        title: "Le coût en temps",
        content:
          "Le traitement de fertilité demande un investissement en temps important qui est rarement intégré dans le coût global. Le planning est imprévisible, et la plupart des rendez-vous tombent sur les heures de bureau.",
        bulletPoints: [
          "Rendez-vous de suivi tous les 2 à 3 jours pendant la stimulation, généralement tôt le matin",
          "Les trajets aller-retour à la clinique s'accumulent, surtout si ta clinique n'est pas à proximité",
          "Jours de récupération après la ponction (la plupart prennent 1 à 2 jours de repos)",
          "La charge mentale — le poids du traitement affecte la productivité et le bien-être",
        ],
      },
      {
        title: "Perte de revenus et impact sur la carrière",
        content:
          "C'est le coût dont presque personne ne parle ouvertement. Prendre du temps pour les rendez-vous, gérer les effets secondaires, et porter le poids émotionnel du traitement affectent tous ton travail. Certaines patientes réduisent leurs heures, refusent des projets, ou retardent des évolutions de carrière pendant le traitement. Il n'y a pas de moyen simple de chiffrer ça, mais c'est réel — et ça vaut le coup de l'intégrer dans tes plans.",
      },
      {
        title: "Le coût de plusieurs cycles",
        content:
          "La plupart des patientes ont besoin de 2 à 3 cycles de FIV pour obtenir une naissance vivante. Sur la Sécu, les 4 tentatives remboursées couvrent la grande majorité des situations. Mais si tu en arrives à un 5e cycle, ou si tu choisis l'étranger pour aller plus vite, le coût cumulé devient un vrai sujet — pense-le sur la durée plutôt que cycle par cycle.",
        bulletPoints: [
          "Nombre moyen de cycles pour une naissance vivante : 2,5 pour les patientes de moins de 35 ans, plus pour les patientes plus âgées",
          "Les 4 cycles couverts par la Sécu suffisent à la plupart des parcours réussis",
          "Les TEC ne consomment pas de tentative supplémentaire — ils s'enchaînent à partir des embryons congelés du cycle de FIV",
          "Constitue une réserve financière pour l'imprévu — soins de support, déplacements, et possible parcours à l'étranger",
        ],
      },
    ],
    references: [
      {
        title:
          "Code de la santé publique — articles L2141-3 et L2141-4 (devenir des embryons surnuméraires)",
        authors: "Légifrance",
        url: "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000043898313/",
        evidenceLevel: "official",
      },
      {
        title: "Mon soutien psy — dispositif de remboursement",
        authors: "Assurance Maladie (ameli.fr)",
        url: "https://www.ameli.fr/assure/remboursements/rembourse/remboursement-seance-psychologue-mon-soutien-psy",
        evidenceLevel: "official",
      },
      {
        title: "Embryons surnuméraires : conservation, don, recherche",
        authors: "Agence de la biomédecine",
        url: "https://www.procreation-medicale.fr/vos-questions/devenir-gametes-embryons-apres-31-mars-2025/",
        evidenceLevel: "official",
      },
      {
        title: "Compléments alimentaires : recommandations et nutrivigilance",
        authors: "ANSES",
        url: "https://www.anses.fr/",
        evidenceLevel: "official",
      },
      {
        title:
          "Rapport médical et scientifique annuel — résultats de l'AMP en France",
        authors: "Agence de la biomédecine",
        url: "https://rams.agence-biomedecine.fr/",
        evidenceLevel: "official",
      },
    ],
  },
});
